import { useState, useEffect } from 'react';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { X, Upload, MapPin, Camera } from 'lucide-react';
import CameraCapture from './CameraCapture';
import { useLanguage } from '@/LanguageContext';
import { getTranslation } from '@/translations';

const JobForm = ({ job, onClose, onSuccess }) => {
  const { language } = useLanguage();
  const t = (key) => getTranslation(key, language);
  
  const [formData, setFormData] = useState({
    address: '',
    description: '',
    client_name: '',
    date: '',
    main_technician: '',
    photos: [],
    price: '',
    status: 'pending',
    invoice_sent: false,
  });
  const [loading, setLoading] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  // Load from localStorage on mount (for auto-save recovery)
  useEffect(() => {
    const savedDraft = localStorage.getItem('jobFormDraft');
    if (savedDraft && !job) {
      try {
        const parsed = JSON.parse(savedDraft);
        setFormData(parsed);
        toast.info('Draft restored');
      } catch (e) {
        console.error('Failed to load draft');
      }
    }
  }, []);

  useEffect(() => {
    if (job) {
      // Handle date properly - extract just the date part without timezone conversion
      const dateValue = job.date ? job.date.split('T')[0] : '';
      
      setFormData({
        address: job.address,
        description: job.description,
        client_name: job.client_name,
        date: dateValue,
        main_technician: job.main_technician,
        photos: job.photos || [],
        price: job.price || '',
        status: job.status || 'pending',
        invoice_sent: job.invoice_sent || false,
      });
    }
  }, [job]);

  // Auto-save to localStorage (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!job && (formData.client_name || formData.address || formData.description)) {
        localStorage.setItem('jobFormDraft', JSON.stringify(formData));
        setLastSaved(new Date());
      }
    }, 2000); // Auto-save after 2 seconds of inactivity

    return () => clearTimeout(timer);
  }, [formData, job]);


  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
        const response = await axiosInstance.post('/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        // Construct full URL
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        // Ensure no double slash if backendUrl ends with /
        const cleanBackendUrl = backendUrl.replace(/\/$/, '');
        return `${cleanBackendUrl}${response.data.url}`;
    } catch (error) {
        console.error('Upload failed', error);
        toast.error('Failed to upload image');
        return null;
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    const maxPhotos = 10;
    
    if (formData.photos.length + files.length > maxPhotos) {
      toast.error(`Maximum ${maxPhotos} photos allowed per job`);
      return;
    }
    
    if (files.length > 0) {
      toast.info(`Uploading ${files.length} photo${files.length > 1 ? 's' : ''}...`);
      setLoading(true);
      
      const newPhotos = [];
      for (const file of files) {
        const url = await uploadFile(file);
        if (url) newPhotos.push(url);
      }
      
      setFormData((prev) => ({
        ...prev,
        photos: [...prev.photos, ...newPhotos],
      }));
      setLoading(false);
      toast.success('Upload complete!');
    }
  };

  // Image compression removed in favor of direct upload

  const removePhoto = (index) => {
    setFormData((prev) => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index),
    }));
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setGettingLocation(true);
    toast.info('Getting your location...');

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          // Call backend to convert coordinates to address
          const response = await axiosInstance.post('/geocode/reverse', {
            latitude,
            longitude,
          });
          
          setFormData((prev) => ({
            ...prev,
            address: response.data.formatted_address,
          }));
          
          toast.success('Address auto-filled from your location!');
        } catch (error) {
          toast.error('Failed to get address from location');
          console.error('Geocoding error:', error);
        } finally {
          setGettingLocation(false);
        }
      },
      (error) => {
        setGettingLocation(false);
        let errorMessage = 'Failed to get your location';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please enable location access.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out';
            break;
          default:
            errorMessage = 'An unknown error occurred';
        }
        
        toast.error(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const handleCameraCapture = async (imageDataUrl) => {
    try {
        setLoading(true);
        const res = await fetch(imageDataUrl);
        const blob = await res.blob();
        const file = new File([blob], `camera_${Date.now()}.jpg`, { type: "image/jpeg" });
        
        const url = await uploadFile(file);
        if (url) {
            setFormData((prev) => ({
              ...prev,
              photos: [...prev.photos, url],
            }));
            toast.success('Photo uploaded!');
        }
    } catch (error) {
        console.error('Camera upload error', error);
        toast.error('Failed to upload camera photo');
    } finally {
        setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate photo count
      if (formData.photos.length > 10) {
        toast.error('Maximum 10 photos allowed per job');
        setLoading(false);
        return;
      }

      // Show loading message for large submissions
      if (formData.photos.length > 3) {
        toast.info('Uploading photos, please wait...');
      }

      // Prepare data for submission
      const submissionData = { ...formData };
      
      // Convert empty price to null to avoid 422 errors
      if (submissionData.price === '') {
        submissionData.price = null;
      }
      
      // Ensure date is YYYY-MM-DD
      if (submissionData.date) {
        submissionData.date = submissionData.date.split('T')[0];
      }

      if (job) {
        // Update existing job
        await axiosInstance.put(`/jobs/${job.id}`, submissionData);
        toast.success('Job updated successfully!');
      } else {
        // Create new job
        await axiosInstance.post('/jobs', submissionData);
        toast.success('Job created successfully!');
        // Clear auto-saved draft on successful creation
        localStorage.removeItem('jobFormDraft');
      }
      onSuccess();
    } catch (error) {
      console.error('Submit error:', error);
      
      // Better error messages
      if (error.code === 'ERR_NETWORK') {
        toast.error('Network error. Please check your connection and try again.');
      } else if (error.response?.status === 413) {
        toast.error('Photos are too large. Please use fewer photos or reduce their size.');
      } else {
        const detail = error.response?.data?.detail;
        if (Array.isArray(detail)) {
            // Handle Pydantic validation errors (list of objects)
            const messages = detail.map(err => `${err.loc.join('.')}: ${err.msg}`).join('\n');
            toast.error(`Validation Error:\n${messages}`);
        } else if (typeof detail === 'object') {
            toast.error(JSON.stringify(detail));
        } else {
            toast.error(detail || 'Failed to save job. Please try again.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto dark:bg-slate-800 dark:text-slate-200" data-testid="job-form-dialog" aria-describedby="job-form-description">
        <DialogHeader>
          <div className="flex justify-between items-center">
            <DialogTitle className="text-2xl font-bold dark:text-slate-100">
              {job ? t('edit_job') : t('create_job')}
            </DialogTitle>
            {!job && lastSaved && (
              <span className="text-xs text-green-600 flex items-center gap-1">
                ✓ {t('auto_saved')} {new Date(lastSaved).toLocaleTimeString()}
              </span>
            )}
          </div>
        </DialogHeader>
        <p id="job-form-description" className="sr-only">
          Fill out the form below to {job ? 'edit' : 'create'} a job with client details, address, and photos.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="client_name">{t('client_name')}</Label>
            <Input
              id="client_name"
              data-testid="client-name-input"
              value={formData.client_name}
              onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
              placeholder={t('client_name')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">{t('address')}</Label>
            <div className="flex gap-2">
              <Input
                id="address"
                data-testid="address-input"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder={t('enter_address')}
                className="flex-1"
              />
              <Button
                type="button"
                data-testid="use-location-button"
                onClick={handleUseMyLocation}
                disabled={gettingLocation}
                variant="outline"
                className="flex items-center gap-2 whitespace-nowrap"
              >
                <MapPin className="w-4 h-4" />
                {gettingLocation ? 'Getting...' : t('use_my_location')}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date">{t('date')}</Label>
              <Input
                id="date"
                data-testid="date-input"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="main_technician">{t('main_technician')}</Label>
              <Input
                id="main_technician"
                data-testid="technician-input"
                value={formData.main_technician}
                onChange={(e) => setFormData({ ...formData, main_technician: e.target.value })}
                placeholder={t('enter_technician')}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">{t('description')}</Label>
            <Textarea
              id="description"
              data-testid="description-input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={t('describe_work')}
              rows={4}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="price">{t('price')}</Label>
            <Input
              id="price"
              data-testid="price-input"
              type="number"
              step="0.01"
              min="0"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              placeholder={t('enter_price')}
            />
          </div>

          {/* Status toggles for editing existing jobs */}
          {job && (
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-base font-semibold">{t('mark_completed')}</Label>
                  <p className="text-xs text-slate-500 mt-1">{t('status')}: {formData.status === 'completed' ? t('completed') : t('pending')}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="status-toggle"
                    checked={formData.status === 'completed'}
                    onChange={(e) => setFormData({ ...formData, status: e.target.checked ? 'completed' : 'pending' })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-base font-semibold">{t('mark_invoice_sent')}</Label>
                  <p className="text-xs text-slate-500 mt-1">{formData.invoice_sent ? t('invoice_sent') : 'Not sent'}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="invoice-toggle"
                    checked={formData.invoice_sent}
                    onChange={(e) => setFormData({ ...formData, invoice_sent: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>
              {t('photos')} ({t('maximum')} {/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? '3 on mobile' : '10'})
            </Label>
            {/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) && (
              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                {t('mobile_tip')}
              </p>
            )}
            <div className="flex gap-2">
              {/* File Upload */}
              <div className="flex-1 border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-sky-400 transition-colors">
                <input
                  type="file"
                  id="photo-upload"
                  data-testid="photo-upload-input"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <label htmlFor="photo-upload" className="cursor-pointer">
                  <Upload className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-600 font-medium">{t('upload_from_device')}</p>
                  <p className="text-xs text-slate-400 mt-1">{t('select_multiple')}</p>
                </label>
              </div>

              {/* Camera Capture */}
              <div className="flex-1 border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-sky-400 transition-colors">
                <button
                  type="button"
                  data-testid="open-camera-button"
                  onClick={() => setShowCamera(true)}
                  className="w-full h-full flex flex-col items-center justify-center cursor-pointer"
                >
                  <Camera className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-600 font-medium">{t('take_photos')}</p>
                  <p className="text-xs text-slate-400 mt-1">{t('use_camera')}</p>
                </button>
              </div>
            </div>

            {formData.photos.length > 0 && (
              <>
                <p className="text-sm text-slate-600 font-medium mt-4">
                  {formData.photos.length} {t('photos_added')}
                </p>
                <div className="photo-preview-grid">
                  {formData.photos.map((photo, index) => (
                    <div key={index} className="photo-preview-item" data-testid={`photo-preview-${index}`}>
                      <img src={photo} alt={`Preview ${index + 1}`} />
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        data-testid={`remove-photo-${index}`}
                        aria-label="Remove photo"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              data-testid="cancel-button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              data-testid="save-job-button"
              className="flex-1 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700"
              disabled={loading}
            >
              {loading ? 'Saving...' : (job ? t('update_job') : t('create_job'))}
            </Button>
          </div>
        </form>
        
        {/* Loading Overlay */}
        {loading && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50 rounded-lg">
            <div className="bg-white p-6 rounded-lg shadow-xl text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-sky-500 border-t-transparent mb-4"></div>
              <p className="text-slate-700 font-medium">
                {formData.photos.length > 0 ? 'Uploading photos...' : 'Saving job...'}
              </p>
              <p className="text-sm text-slate-500 mt-2">Please wait, do not close this window</p>
            </div>
          </div>
        )}
      </DialogContent>

      {/* Camera Capture Modal */}
      {showCamera && (
        <CameraCapture
          onCapture={handleCameraCapture}
          onClose={() => setShowCamera(false)}
        />
      )}
    </Dialog>
  );
};

export default JobForm;
